/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { DebtorCollectionDto2 } from '@/api-sdk';
import { placeholderBackup } from '@/assets';
import { collectionListStore } from '@/stores/store.collection-list';
import { baseStyles, typographyStyles } from '@/styles';
import { getCustomerName, i18nInit, setBaseUrl } from '@/utils';
import { iconTrashCan } from '../icons';

import '@/components/accessibility/fg-aria-live';
import '@/components/filters/fg-debtor-list-filters';
import '@/components/image/fg-image';
import '@/components/messages/fg-message';

@customElement('fg-collection-list')
export class FgCollectionList extends LitElement {
  private collectionListStore = collectionListStore.getState();
  private _storeUnsub?: () => void;

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  locale = 'en_US';

  @property({ type: String })
  country = 'NL';

  @property({ type: String })
  debtor = '';

  @property({ type: String })
  customerId!: string;

  @property({ type: Object })
  customers: { [id: string]: string } = {};

  @property({ type: String })
  collectionDetailUrl = '';

  @property({ type: Array })
  collections?: DebtorCollectionDto2[] = this.collectionListStore.collections;

  @property({ type: Number })
  currentPage = this.collectionListStore.page;

  @property({ type: Number })
  pages = this.collectionListStore.pages;

  @property({ type: Boolean })
  isLoading = this.collectionListStore.isLoading;

  @property({ type: String })
  announcement = this.collectionListStore.announcement;

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  connectedCallback() {
    super.connectedCallback();

    this._storeUnsub = collectionListStore.subscribe(state => {
      this.collections = state.collections;
      this.pages = state.pages;
      this.currentPage = state.page;
      this.isLoading = state.isLoading;
      this.announcement = state.announcement;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._storeUnsub?.();
  }

  willUpdate(changedProperties: PropertyValues) {
    const { setConfig, getCollections } = this.collectionListStore;

    if (
      changedProperties.has('baseUrl') ||
      changedProperties.has('locale') ||
      changedProperties.has('country') ||
      changedProperties.has('debtor') ||
      changedProperties.has('customerId') ||
      changedProperties.has('customers')
    ) {
      setConfig({
        baseUrl: this.baseUrl,
        locale: this.locale,
        country: this.country,
        debtor: this.debtor,
        customerId: this.customerId ? parseInt(this.customerId) : undefined,
        customers: this.customers,
      });
    }

    if (this.collections === undefined) {
      getCollections();
    }
  }

  handleFilter(
    event: CustomEvent<{ searchString: string; createdByMe: boolean }>,
  ) {
    const { searchString, createdByMe } = event.detail;
    const { handleFilter } = this.collectionListStore;
    handleFilter(searchString, createdByMe);
  }

  handleGoToPage(event: CustomEvent<{ page: number }>) {
    const { page } = event.detail;
    const { goToPage } = this.collectionListStore;
    goToPage(page);
  }

  handleDeleteCollection(collectionId: string, collectionName: string) {
    const { deleteCollection } = this.collectionListStore;
    deleteCollection(collectionId, collectionName);
  }

  render() {
    return html`
      <fg-aria-live .announcement=${this.announcement}></fg-aria-live>
      <fg-debtor-list-filters
        part="wholesale"
        .searchInputPlaceholder=${'content.collection.searchPlaceholderCollections'}
        @filter=${this.handleFilter}
      ></fg-debtor-list-filters>
      ${this.collections && this.collections.length > 0 && !this.isLoading
        ? html`
            <ul>
              ${this.collections.map(collection => {
                return html`
                  <li>
                    <a href="${this.collectionDetailUrl}/${collection.id}">
                      <fg-image
                        src=${collection?.imageUrl || placeholderBackup}
                        width="70"
                        height="70"
                        .placeholder=${placeholderBackup}
                        alt=""
                      ></fg-image>
                      <div>
                        <strong>${collection.name}</strong>
                        <p>
                          ${t('content.collection.productCount', {
                            count: collection.count,
                          })}${getCustomerName(
                            this.customers,
                            collection.createdBy,
                          )
                            ? `,`
                            : nothing}
                          ${getCustomerName(
                            this.customers,
                            collection.createdBy,
                          )
                            ? html`
                                ${t('content.collection.createdBy', {
                                  customerName: getCustomerName(
                                    this.customers,
                                    collection.createdBy,
                                  ),
                                })}
                              `
                            : nothing}
                        </p>
                      </div>
                    </a>
                    <button
                      @click=${() =>
                        this.handleDeleteCollection(
                          collection.id,
                          collection.name,
                        )}
                    >
                      ${iconTrashCan}
                      <span class="sr-only">
                        ${t('accessibility.deleteCollection', {
                          collectionName: collection.name,
                        })}
                      </span>
                    </button>
                  </li>
                `;
              })}
            </ul>
          `
        : this.collections?.length === 0 && !this.isLoading
          ? html`
              <fg-message
                part="wholesale"
                .message=${t('content.collection.noCollections') || ''}
                type="info"
              ></fg-message>
            `
          : html`
              ${Array.from({ length: 8 }).map(
                () => html`
                  <div class="loading-placeholder">
                    <div class="collection-image"></div>
                    <div class="collection-details">
                      <span class="collection-name"></span>
                      <span class="collection-description"></span>
                    </div>
                  </div>
                `,
              )}
            `}
      ${this.pages > 1
        ? html`
            <fg-pagination
              part="wholesale"
              .pages=${this.pages}
              .currentPage=${this.currentPage}
              @go-to-page=${this.handleGoToPage}
            ></fg-pagination>
          `
        : nothing}
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    css`
      ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        flex-direction: column;
      }

      li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        padding: 0.5rem;
        padding: 0.5rem 1rem 0.5rem 0.5rem;
      }

      a {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        color: var(--fg-black, var(--fg-fallback-black));
        text-decoration: none;
        color: var(--fg-gray--900, var(--fg-fallback-gray--900));
        flex-grow: 1;
        &:hover strong {
          text-decoration: underline;
        }
      }

      strong {
        font-size: 1.125rem;
        line-height: 160%;
        font-weight: 600;
      }

      p {
        font-size: 14px;
        line-height: 160%;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
      }

      fg-image {
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        background-color: #ffffff;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      fg-image::part(image) {
        aspect-ratio: 1 / 1;
        width: 70px;
        height: 70px;
        object-fit: contain;
      }

      button {
        background: none;
        border: none;
        cursor: pointer;
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
        && svg {
          height: 26px;
          width: 14px;
        }
      }

      fg-pagination {
        margin-top: 1rem;
      }

      @keyframes blink {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }

      .loading-placeholder {
        width: 100%;
        display: flex;
        flex-direction: row;
        gap: 0.75rem;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        align-items: center;
        padding: 0.5rem;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );

        &:not(:last-child) {
          margin-bottom: 0.5rem;
        }

        & .collection-image {
          background-color: var(--fg-gray--200, var(--fg-fallback-gray--200));
          border-radius: var(
            --fg-borderradius--xs,
            var(--fg-fallback-borderradius--xs)
          );

          &:before {
            display: block;
            content: '';
            width: 70px;
            height: 70px;
          }
        }

        & .collection-name {
          display: block;
          background: var(--fg-gray--200, var(--fg-fallback-gray--200));
          border-radius: 0.5125rem;
          height: 1.25rem;
          width: 10rem;
        }

        & .collection-description {
          display: block;
          background: var(--fg-gray--200, var(--fg-fallback-gray--200));
          border-radius: 0.5rem;
          margin-top: 0.5rem;
          height: 1rem;
          width: 15rem;
        }
      }

      .no-collections-message {
        text-align: center;
        font-size: 1.125rem;
        line-height: 160%;
        font-weight: 600;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
      }
    `,
  ];
}
